<template>
  <BasePage>
    <template #page-header:subtitle>
      <BackButton class="mr-4" ref="backButton" :fallback-to="{ name: 'clients' }" />
      {{ postOrder?.site }} - Post Orders
    </template>

    <template #page-header:bottom>
      <ErrorAlert
        v-if="error != null"
        dense
        :error="error"
        @clearErrors="clearErrors"
        class="mb-0 mt-4 rounded"
      />
    </template>

    <v-card
      height="calc(100vh - 226px)"
      color="background"
      flat
      class="d-flex flex-column rounded-lg mt-1"
      :loading="isPostOrderLoading"
    >
      <v-sheet
        variant="flat"
        density="compact"
        min-height="90"
        color="background"
        class="d-flex flex-column align-stretch justify-center"
      >
        <v-fade-transition mode="out-in">
          <v-progress-circular
            v-if="isInstructionsPending"
            indeterminate
            class="align-self-center"
            color="secondary"
            width="2"
            size="32"
          />
          <div v-else class="pa-1 post-order-instructions-tab">
            <PostOrderInstructionMenuDropDown
              :post-order-id="postOrderId"
              :is-instruction-type-selected="
                (selectedInstruction &&
                  selectedInstruction.type === PostOrderInstructionTypeEnum.Start) ||
                noInstructionStateType === PostOrderInstructionTypeEnum.Start
              "
              :instructions="
                postOrderInstructions?.filter(
                  (instruction) => instruction.type == PostOrderInstructionTypeEnum.Start
                )
              "
              :instruction-type="PostOrderInstructionTypeEnum.Start"
              @no-instruction-selected="displayNoInstructionState"
              @select-instruction="setSelectedInstruction"
            />

            <v-divider />

            <PostOrderInstructionMenuDropDown
              :post-order-id="postOrderId"
              :is-instruction-type-selected="
                (selectedInstruction &&
                  selectedInstruction.type === PostOrderInstructionTypeEnum.Patrol) ||
                noInstructionStateType === PostOrderInstructionTypeEnum.Patrol
              "
              :instructions="
                postOrderInstructions?.filter(
                  (instruction) => instruction.type == PostOrderInstructionTypeEnum.Patrol
                )
              "
              :instruction-type="PostOrderInstructionTypeEnum.Patrol"
              @create-instruction="
                openPostOrderInstructionCreateFormDialog(PostOrderInstructionTypeEnum.Patrol)
              "
              @no-instruction-selected="displayNoInstructionState"
              @select-instruction="setSelectedInstruction"
              @instruction-deleted="handleInstructionDeleted"
            />

            <v-divider />

            <PostOrderInstructionMenuDropDown
              :post-order-id="postOrderId"
              :is-instruction-type-selected="
                (selectedInstruction &&
                  selectedInstruction.type === PostOrderInstructionTypeEnum.End) ||
                noInstructionStateType === PostOrderInstructionTypeEnum.End
              "
              :instruction="selectedInstructionRevision"
              :instructions="
                postOrderInstructions?.filter(
                  (instruction) => instruction.type == PostOrderInstructionTypeEnum.End
                )
              "
              :instruction-type="PostOrderInstructionTypeEnum.End"
              @no-instruction-selected="displayNoInstructionState"
              @select-instruction="setSelectedInstruction"
            />
          </div>
        </v-fade-transition>
      </v-sheet>

      <v-dialog v-model="isFullscreenActive" transition="fade-transition" fullscreen eager>
        <template v-slot:default>
          <v-card>
            <div id="fullscreen-instruction-detail"></div>
          </v-card>
        </template>
      </v-dialog>

      <!-- Single component instance that teleports to fullscreen when needed -->
      <Teleport to="#fullscreen-instruction-detail" :disabled="!isFullscreenActive">
        <PostOrderInstructionDetail
          :post-order-id="postOrderId"
          :instruction="selectedInstruction"
          :loading="isInstructionsPending || isPostOrderLoading"
          :no-data-header="
            noInstructionStateType != null
              ? 'No instructions available'
              : `No instruction${(postOrderInstructions ?? []).length > 0 ? ' selected' : 's available'} `
          "
          @edit-instruction="openPostOrderInstructionEditFormDialog"
        >
          <template #no-data-description>
            <p class="mt-0 mb-6 text-caption text-medium-emphasis">
              <span>
                <template v-if="noInstructionStateType != null">
                  You do not have a `<strong class="text-primary">{{
                    postOrderInstructionTypeDisplayRepresentation(noInstructionStateType)
                  }}</strong
                  >` instruction for this post order. Create a
                  <strong class="text-primary">{{
                    postOrderInstructionTypeDisplayRepresentation(noInstructionStateType)
                  }}</strong>
                  instruction here</template
                >
                <template v-else-if="postOrderInstructions && postOrderInstructions!.length > 0">
                  You do not have an instruction selected. Please select one from the instruction
                  drop down above
                </template>
              </span>
            </p>
          </template>

          <template #no-primary-action="{ instruction, noValidRevisions }">
            <v-btn
              v-if="
                noInstructionStateType != null ||
                (instruction &&
                  noValidRevisions &&
                  currentUser?.hasPermission('post_orders.add_postorderinstructions'))
              "
              color="primary"
              depressed
              @click="
                noValidRevisions && instruction
                  ? createRevisionFromInstruction(instruction)
                  : createPostOrderInstructionFromType(
                      instruction ? instruction.type! : noInstructionStateType!
                    )
              "
              :loading="
                useCreateInstructionMutation.isPending.value ||
                useCreateInstructionRevisionMutation.isPending.value
              "
            >
              <template #prepend>
                <v-icon left class="hidden-sm-and-down"> mdi-plus </v-icon>
              </template>
              Create
              {{
                postOrderInstructionTypeDisplayRepresentation(
                  instruction ? instruction.type! : noInstructionStateType!
                )
              }}
              Instruction
            </v-btn>
          </template>
        </PostOrderInstructionDetail>
      </Teleport>
    </v-card>
  </BasePage>

  <PostOrderInstructionFormDialog
    v-if="selectedFormInstruction"
    v-model:dialog="isPostOrderInstructionFormDialogActive"
    :post-order-id="postOrderId"
    :post-order-instruction="selectedFormInstruction"
    :is-edit="isEditingInstruction"
    @select-instruction="setSelectedInstruction"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, provide, ref, watch } from 'vue'
import { cloneDeep } from 'lodash'

import { VBtn } from 'vuetify/components'
import { type ISystemError } from '@/models/error'
import { PostOrderFullscreenSymbol } from '@/components/post-order/postOrderProvide'

import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { useQueryClient } from '@tanstack/vue-query'
import {
  useCreatePostOrderInstruction,
  useFetchPostOrder,
  useCreatePostOrderInstructionRevision,
  useFetchPostOrderInstructions
} from '@/composables/post-order'

import {
  PostOrderInstruction,
  PostOrderInstructionTypeEnum,
  type IPostOrderInstruction,
  type IPostOrderInstructionData,
  type IPostOrderInstructionRevision
} from '@/models/post-order'

import BasePage from '@/components/base/BasePage.vue'
import BackButton from '@/components/common/BackButton.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'
import PostOrderInstructionDetail from '@/components/post-order/PostOrderInstructionDetail.vue'
import PostOrderInstructionFormDialog from '@/components/post-order/PostOrderInstructionFormDialog.vue'
import PostOrderInstructionMenuDropDown from '@/components/post-order/PostOrderInstructionMenuDropDown.vue'

const route = useRoute()
const router = useRouter()

// Define a type for the query parameters
type QueryParams = {
  instruction?: number | null
}

// Function to update query parameters with type safety
function updateRouteQuery(newQueryParams: Partial<QueryParams>): void {
  router.push({ query: { ...route.query, ...newQueryParams } })
}

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

// data
const postOrderId = computed(() => Number(route.params.id))

const selectedInstruction = ref<null | IPostOrderInstruction>(null)

const selectedInstructionRevision = ref<null | IPostOrderInstructionRevision>(null)

const noInstructionStateType = ref<PostOrderInstructionTypeEnum | null>(null)

function displayNoInstructionState(type: PostOrderInstructionTypeEnum) {
  noInstructionStateType.value = type

  selectedInstruction.value = null
}

function setSelectedInstruction(instruction: IPostOrderInstruction) {
  selectedInstruction.value = instruction

  noInstructionStateType.value = null

  updateRouteQuery({ instruction: instruction.id })
}

function handleInstructionDeleted(instructionId: number) {
  // If the deleted instruction was selected, clear the selection
  if (selectedInstruction.value?.id === instructionId) {
    selectedInstruction.value = null
    displayNoInstructionState(PostOrderInstructionTypeEnum.Patrol)
  }
}

const queryClient = useQueryClient()

const {
  postOrder,
  isPending: isPostOrderPending,
  error: postOrderFetchError
} = useFetchPostOrder(postOrderId)

const isPostOrderLoading = computed(() => isPostOrderPending.value)

const {
  instructions: postOrderInstructions,
  isLoading: isInstructionsPending,
  error: instructionsFetchError,
  queryKey: instructionsQueryKey
} = useFetchPostOrderInstructions(postOrderId)

watch([instructionsFetchError, postOrderFetchError], ([instructionError], [postOrderError]) => {
  if (instructionError || postOrderError) {
    error.value = instructionError || postOrderError
  }
})

const instructionRouteQuery = computed(() => Number(route.query.instruction))

// update error to crud action error display
watch(
  postOrderInstructions,
  (value) => {
    if (value) {
      selectInstructionFromPostOrderInstructionList(value)
    }
  },
  {
    deep: true
  }
)

watch(
  route,
  () => {
    if (postOrderInstructions.value) {
      selectInstructionFromPostOrderInstructionList(postOrderInstructions.value)
    }
  },
  {
    immediate: true
  }
)

function selectInstructionFromPostOrderInstructionList(instructions: IPostOrderInstruction[]) {
  if (instructions.length == 0) {
    displayNoInstructionState(PostOrderInstructionTypeEnum.Start)
  } else {
    let foundInstruction = null as IPostOrderInstruction | null

    // prioritize route query instead of current selected instruction
    const filterQuery = instructionRouteQuery.value
      ? instructionRouteQuery.value
      : selectedInstructionRevision.value
        ? selectedInstructionRevision.value.id!
        : null

    foundInstruction = instructions.find((instruction) => instruction.id == filterQuery) ?? null

    if (foundInstruction) {
      setSelectedInstruction(foundInstruction)
    } else {
      // first post order instruction
      setSelectedInstruction(instructions[0])
    }
  }
}

const isEditingInstruction = ref(false)
const selectedFormInstruction = ref<IPostOrderInstructionData | null>(null)
const isPostOrderInstructionFormDialogActive = ref(false)

function openPostOrderInstructionCreateFormDialog(type: PostOrderInstructionTypeEnum) {
  selectedFormInstruction.value = {
    type: type,
    form: {
      title: postOrderInstructionTypeDisplayRepresentation(type),
      description: `Instruction for ${postOrderInstructionTypeDisplayRepresentation(type)}`
    },
    order: (postOrderInstructions.value?.length ?? 0) + 1
  }

  //Hack
  // rendering the form dialog after the selectedFormInstruction is set will not set the prop in the component
  nextTick(() => {
    isEditingInstruction.value = false
    isPostOrderInstructionFormDialogActive.value = true
  })
}

function openPostOrderInstructionEditFormDialog(instruction: IPostOrderInstruction) {
  selectedFormInstruction.value = cloneDeep(instruction)

  //Hack
  // rendering the form dialog after the selectedFormInstruction is set will not set the prop in the component
  nextTick(() => {
    isEditingInstruction.value = true
    isPostOrderInstructionFormDialogActive.value = true
  })
}

const error = ref<ISystemError | null>(null)

function clearErrors() {
  error.value = null
}

function postOrderInstructionTypeDisplayRepresentation(type: PostOrderInstructionTypeEnum) {
  switch (type) {
    case PostOrderInstructionTypeEnum.Start:
      return 'Start of shift'
    case PostOrderInstructionTypeEnum.Patrol:
      return 'Patrol'
    case PostOrderInstructionTypeEnum.End:
      return 'End of shift'
  }
}

const useCreateInstructionMutation = useCreatePostOrderInstruction(postOrderId)

const useCreateInstructionRevisionMutation = useCreatePostOrderInstructionRevision()

function createRevisionFromInstruction(instruction: IPostOrderInstruction) {
  return useCreateInstructionRevisionMutation.mutateAsync(
    {
      postOrderId: postOrderId.value,
      instructionId: instruction.id!,
      revision: {
        // Must be passed as an empty object to create a new revision due to the API requirements
        template: {}
      }
    },
    {
      async onSuccess() {
        // wait on refreshing instructions before is loading is stopped
        await queryClient.invalidateQueries({
          queryKey: instructionsQueryKey
        })

        // then select instruction to display
        setSelectedInstruction(instruction)
      },
      onError(err) {
        console.error('Error creating post order instruction revision', err)
        error.value = err
      }
    }
  )
}

function createPostOrderInstructionFromType(type: PostOrderInstructionTypeEnum) {
  const instructionData: IPostOrderInstructionData = {
    form: {
      title: postOrderInstructionTypeDisplayRepresentation(type),
      description: `Instruction for ${postOrderInstructionTypeDisplayRepresentation(type)}`
    },
    // TODO: LH - 2025-08-02 - sync with Ricky to not have to send this in the request
    post_order: postOrderId.value,
    type: type,
    order: 1
  }

  useCreateInstructionMutation.mutate(instructionData, {
    async onSuccess(data) {
      const postOrderInstruction = new PostOrderInstruction(data)

      await createRevisionFromInstruction(postOrderInstruction)
    },
    onError(err) {
      console.error('Error creating post order instruction', err)
      error.value = err
    }
  })
}

// FULLSCREEN STATE
const isFullscreenActive = ref(false)

const isFullscreen = computed(() => isFullscreenActive.value)

function setFullscreen(value: boolean) {
  isFullscreenActive.value = value
}

provide(PostOrderFullscreenSymbol, {
  isFullscreen,
  setFullscreen
})
</script>

<style lang="scss">
.post-order-instructions-tab {
  align-items: center;
  display: flex;
  position: relative;
  overflow-x: auto;
  justify-content: space-between;
  z-index: 1;
}

#instruction-detail {
  overflow-y: auto;
}
</style>
